import * as React from "react";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { BaseComponent } from "../../../../../../../react-core/Html/BaseComponent";
import { AssetClassType } from "../../../../../../../Wrap/Constants/Enums/Domain/Instrument/AssetClassType";
import { SubAccountType } from "../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { Money } from "../../../../../../../Wrap/Core/DataTypes/Money";
import { InvestmentsColumns } from "../../../../../../../Wrap/Models/Domains/Users/UserPageColumnSetting/InvestmentsColumns";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { Contents } from "../Insights/Resource/Contents";
import { CommonViewTbody } from "./CommonViewTbody";
import { InvestmentsColConfigHelper } from "../../../Common/InvestmentDetailsPopUp/InvestmentsColConfigHelper";
import { IInvestmentTableData, ITotal } from "./Store/InvestmentsStatic";
import { PresentationContext } from "../Common/PresentationViewWrapper";
import CoachMarker from "../../../../../../Shared/CoachMarkers/CoachMarker";
import { StringHelper } from "../../../../../../../react-core/Helpers/StringHelper";
import { InvestmentsAction } from "./Action/InvestmentAction";
import { PortfolioDashboardStore } from "../../Store/Variable";
import { SubAccount } from "../../../../../../../Wrap/Models/Domains/Account/SlAccounts/SubAccount";
import AdviserSiteTagManager, {
    eventCategoryForGTM,
} from "../../../../../../Shared/Cookies/ChoicesCookie/AdviserSiteTagManager";
import { DepositProduct } from "../../../../../../../Wrap/Models/Domains/Investment/DepositProduct";

interface IInvestmentTableProps {
    data: IInvestmentTableData;
    asAt: Date;
    isAssetView: boolean;
    assetClassType: AssetClassType;
    cols: any[];
    onChange: (data: IInvestmentTableData) => void;
    cashProducts: Record<number, DepositProduct>;
}

interface IInvestmentTableState {
    isEditTreasure: boolean;
}

export class InvestmentTable extends BaseComponent<IInvestmentTableProps, IInvestmentTableState> {
    constructor(props: IInvestmentTableProps) {
        super(props);
        this.state = {
            isEditTreasure: false,
        };
    }

    private addEvent = new AdviserSiteTagManager().addEvent;

    private getMoneyFormat(val: Money, isShowCurrency?: boolean, decimalCount?: number): string {
        let formatMoney = MoneyDisplayHelper.FromatMoneyDisplay(
            Math.abs(val.Amount),
            val.Currency,
            isShowCurrency,
            decimalCount
        );
        if (val.Amount < 0) {
            formatMoney = "-" + formatMoney;
        }
        return formatMoney;
    }

    private renderTfooter(): JSX.Element {
        const { total, data } = this.props.data;
        const { cols } = this.props;
        if (data && cols && total && data.length > 0) {
            return (
                <tfoot>
                    <tr className="total">
                        <td>Total</td>
                        {cols.map((col, index) => {
                            if (col.Enabled) {
                                return this.renderTfooterRow(col.Column, total);
                            }
                        })}
                    </tr>
                </tfoot>
            );
        } else {
            return null;
        }
    }

    private renderTfooterRow(col: InvestmentsColumns, total: ITotal) {
        switch (col) {
            case InvestmentsColumns.Allocation:
                return <td className="cell-right">{(total.Allocation * 100).toFixed(2)}%</td>;
            case InvestmentsColumns.TotalCost:
                return <td className="cell-right">{this.getMoneyFormat(total.TotalCost)}</td>;
            case InvestmentsColumns.MarketValue:
                return <td className="cell-right">{this.getMoneyFormat(total.MarketValue)}</td>;
            case InvestmentsColumns.Difference:
                const differenceColor =
                    total.Difference.Amount == 0
                        ? " text-bold text-highlight "
                        : total.Difference.Amount > 0
                        ? " text-bold text-positive "
                        : " text-bold text-negative ";
                return (
                    <td className="cell-right">
                        <span className={differenceColor}>{this.getMoneyFormat(total.Difference)}</span>
                    </td>
                );
            case InvestmentsColumns.Quantity:
            case InvestmentsColumns.Location:
            case InvestmentsColumns.UnitCost:
            case InvestmentsColumns.UnitPrice:
            case InvestmentsColumns.FundCode:
            case InvestmentsColumns.TargetAllocation:
                return <td className="cell-right">-</td>;
            case InvestmentsColumns.TreasuredAsset:
                return <td></td>;
        }
    }

    private renderTbody(): JSX.Element[] {
        let { data } = this.props.data;
        const { asAt, isAssetView, assetClassType, cols } = this.props;
        const stringHelper = new StringHelper();
        const tbodyElements: JSX.Element[] = [];
        if (data && data.length > 0) {
            if (!isAssetView) {
                const wrapcash = data.filter((o) => o.WrapperType === SubAccountType.WrapCash);
                if (wrapcash && wrapcash.length > 0) {
                    const orderedDetails = data.filter((o) => o.WrapperType !== SubAccountType.WrapCash);
                    orderedDetails.push(...wrapcash);
                    data = orderedDetails;
                }
            }
            data.map((item, index) => {
                if (item.total.MarketValue?.Amount != 0 || (item.ClAccountId == "" && isAssetView)) {
                    tbodyElements.push(
                        <CommonViewTbody
                            isAssetView={isAssetView}
                            keyName={`${isAssetView ? "InvestmentAssetView" : "ProductInvestment"}Details_${index}`}
                            key={`${isAssetView ? "InvestmentAssetView" : "ProductInvestment"}Details_${index}`}
                            item={item}
                            asAtDate={asAt}
                            assetClassType={assetClassType}
                            cols={cols}
                            stringHelper={stringHelper}
                            isEditTreasure={this.state.isEditTreasure}
                            onTreasuredAssetChange={(productId, value, subAccountId) => {
                                const findSubAccountIdData = this.props.data?.data?.find(
                                    (ele) => ele.ClAccountId == subAccountId
                                );
                                const data = findSubAccountIdData?.EditTreasureAssets;
                                const modelModelPortfolioInvestments =
                                    findSubAccountIdData?.ModelPortfolioInvestments?.data;
                                const modelInstrumentCode = modelModelPortfolioInvestments[0]?.Investments?.find(
                                    (f) => f.ProductId == productId
                                )?.InstrumentCode;
                                const findModelProductId = findSubAccountIdData?.InstrumentWeighting?.find(
                                    (f) => f.InstrumentCode == modelInstrumentCode
                                )?.ProductId;

                                if (value) {
                                    data.push(productId);
                                    if (findModelProductId) {data.push(findModelProductId);}
                                } else {
                                    let newData = data.filter((item) => item != productId);
                                    if (findModelProductId)
                                        {newData = newData.filter((item) => item != findModelProductId);}
                                    this.props.data.data.find(
                                        (ele) => ele.ClAccountId == subAccountId
                                    ).EditTreasureAssets = newData;
                                }
                                this.props.onChange(this.props.data);
                            }}
                            cashProducts={this.props.cashProducts}
                        />
                    );
                }
            });
        }

        return tbodyElements;
    }

    private clearTreasureAssets = () => {
        this.props.data.data.forEach((ele) => {
            ele.EditTreasureAssets = ele.TreasuredAssets.concat();
        });
        this.props.onChange(this.props.data);
    };

    private setTreasureAssets = () => {
        this.props.data.data.forEach((ele) => {
            ele.TreasuredAssets = ele.EditTreasureAssets.concat();
        });
        this.props.onChange(this.props.data);
    };

    public render(): JSX.Element {
        const { data, total } = this.props.data;
        const { cols } = this.props;
        if (!data || data.length == 0 || total.MarketValue.Amount == 0) {
            return <Alert alertType={AlertType.Warning} message={Contents.noDataErrorMessage} />;
        }

        return (
            <>
                <table className="investments-table pb-60">
                    <caption className="sr-only">Investments</caption>
                    <thead>
                        <tr>
                            {cols.map((col, index) => {
                                const tableHeaderCSS =
                                    col.Column == InvestmentsColumns.Investments ? "cell-30" : "cell-right cell-10";
                                if (col.Enabled) {
                                    return (
                                        <th className={tableHeaderCSS} key={`asset-header-${index}`}>
                                            {col.Column == InvestmentsColumns.Investments && (
                                                <CoachMarker coachMarkerKey="investment-table-name" />
                                            )}
                                            {InvestmentsColConfigHelper.getTitle(col.Column, () => {
                                                if (this.state.isEditTreasure) {
                                                    this.clearTreasureAssets();
                                                }
                                                this.addEvent({
                                                    event: "investmentstab_interaction",
                                                    eventParameters: {
                                                        eventCategory: eventCategoryForGTM.investmentsTab,
                                                        eventAction: "interactions",
                                                        eventLabel: "protect assets",
                                                    },
                                                });
                                                this.setState({ isEditTreasure: !this.state.isEditTreasure });
                                            })}
                                        </th>
                                    );
                                }
                            })}
                        </tr>
                    </thead>
                    {this.renderTbody()}
                    {this.renderTfooter()}
                </table>
                {this.state.isEditTreasure && (
                    <div className="row mt-10">
                        <div className="col-12">
                            <button
                                className="btn primary mr-10"
                                onClick={() => {
                                    this.props.data?.data.forEach((item) => {
                                        const allAccounts = PortfolioDashboardStore.getData();
                                        let subAccount: SubAccount = null;

                                        allAccounts.forEach((x) => {
                                            const _subAccount = x.SubAccounts.find(
                                                (y) => y.ClAccountId == item.ClAccountId
                                            );
                                            if (_subAccount) {subAccount = _subAccount;}
                                        });
                                        InvestmentsAction.setSubAccountTreasuredAssetsProductIds(
                                            subAccount.HierarchyId,
                                            item.EditTreasureAssets
                                        );
                                    });
                                    this.setState({ isEditTreasure: false });
                                    this.setTreasureAssets();
                                }}
                            >
                                Save
                            </button>
                            <button
                                className="btn primary mr-10"
                                onClick={() => {
                                    this.setState({ isEditTreasure: false });
                                    this.clearTreasureAssets();
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </>
        );
    }
}
InvestmentTable.contextType = PresentationContext;
